import "./commodities-grid.css";

import type {
	GridColumnProps,
	GridColumnReorderEvent,
	GridColumnResizeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { useCallback, useEffect, useMemo, useState } from "react";

import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useAutoResizeColumns } from "~/shared/hooks/use-auto-resize-columns.ts";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import type { Commodity } from "~/shared/schemas/commodity-schema.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { transformCommodityForSearch } from "~/shared/utils/commodity-search-utils.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { useCommoditiesColumns } from "../../hooks/use-commodities-columns.ts";
import type { CommodityWithDefault } from "../../hooks/use-commodities-data.ts";
import { useCommoditiesData } from "../../hooks/use-commodities-data.ts";
import { useCommodityDeletion } from "../../hooks/use-commodity-deletion.ts";
import { useCommodityEditing } from "../../hooks/use-commodity-editing.ts";
import { useCommoditySelection } from "../../hooks/use-commodity-selection.ts";

const getStableColumnId = (col: GridColumnProps, index: number): string => {
	return (
		(col.id ? String(col.id) : "") ||
		(col.field ? String(col.field) : "") ||
		(col.title ? String(col.title) : "") ||
		`col-${index}`
	);
};

type CommoditiesGridProps = {
	readonly customerId: string;
	readonly onSetSelectedItems?: (rows: Commodity[]) => void;
	readonly onAddAndClose?: (dataItem: Commodity) => void;
	readonly onEditModeChange?: (isEditMode: boolean) => void;
	readonly resetOn?: boolean;
	readonly isModal?: boolean;
	readonly visibleColumns?: BoardColumn[];
	readonly prefsGridKey?: number;
	readonly onColumnReorder?: (event: GridColumnReorderEvent) => void;
	readonly onColumnResize?: (event: GridColumnResizeEvent) => void;
	readonly className?: string;
	readonly style?: React.CSSProperties;
};

export const CommoditiesGrid = ({
	customerId,
	onSetSelectedItems,
	onAddAndClose,
	onEditModeChange,
	resetOn = false,
	isModal = false,
	visibleColumns,
	prefsGridKey = 0,
	onColumnReorder,
	onColumnResize,
	className,
	style,
}: CommoditiesGridProps) => {
	const [reorderGuardKey, setReorderGuardKey] = useState(0);

	// Centralized grid state for pagination, sorting, and filtering
	// This handles itemsPerPage persistence automatically
	const {
		skip,
		take,
		handlePageChange,
		sort,
		handleSortChange,
		filter,
		handleFilterChange,
		isLoadingPreference,
	} = useGridState(GridIdentifiers.Commodities, { enableFilter: true });

	const {
		data,
		setData,
		isLoading,
		setIsLoading,
		unitTypes,
		freightClassData,
		createUserConfig,
		deleteUserConfig,
		userConfigs,
		employeeId,
	} = useCommoditiesData(customerId);

	const {
		select,
		allDataSelected,
		selectedItems,
		handleCellSelect,
		handleHeaderCellSelect,
		setAllDataSelected,
	} = useCommoditySelection({ data, resetOn });

	const {
		edit,
		isEditMode,
		enterEdit,
		handleSaveEdit,
		handleCancelEdit,
		itemChange,
	} = useCommodityEditing({ data, setData, setIsLoading, resetOn });

	const {
		showConfirmation,
		itemToDelete,
		handleRemoveFromList,
		handleConfirmDelete,
		handleCancelDelete,
	} = useCommodityDeletion({
		setData,
		setIsLoading,
		resetOn,
		skip,
		take,
		totalItems: data.length,
	});

	const addDefault = useCallback(
		async (id: number) => {
			await createUserConfig({
				id: 0,
				customerId: Number(customerId),
				employeeId: Number(employeeId),
				configType: ConfigurationType.CommodityDefault,
				configValue: String(id),
			});
		},
		[customerId, employeeId, createUserConfig],
	);

	const removeDefault = useCallback(
		async (id: number) => {
			const config = userConfigs.find(
				(c) =>
					c.configType === ConfigurationType.CommodityDefault &&
					c.customerId === Number(customerId) &&
					c.configValue === String(id),
			);
			if (config) {
				await deleteUserConfig(config.id);
			}
		},
		[customerId, deleteUserConfig, userConfigs],
	);

	const handleToggleDefault = useCallback(
		async (item: CommodityWithDefault, shouldBeDefault: boolean) => {
			await (shouldBeDefault ? addDefault(item.id) : removeDefault(item.id));
		},
		[addDefault, removeDefault],
	);

	// Link selected items back up if provided
	useEffect(() => {
		if (onSetSelectedItems) {
			onSetSelectedItems(selectedItems);
		}
	}, [selectedItems, onSetSelectedItems]);

	useEffect(() => {
		if (onEditModeChange) {
			onEditModeChange(isEditMode);
		}
	}, [isEditMode, onEditModeChange]);

	const state = useMemo(
		() => ({
			isEditMode,
			isLoading,
			edit,
			select,
			allDataSelected,
			showSelection: isModal,
			isModal,
		}),
		[isEditMode, isLoading, edit, select, allDataSelected, isModal],
	);

	const columnData = useMemo(
		() => ({
			freightClassData: freightClassData ?? [],
			unitTypes: unitTypes ?? [],
		}),
		[freightClassData, unitTypes],
	);

	const handlers = useMemo(
		() => ({
			selection: {
				handleCellSelect,
				handleHeaderCellSelect,
				setAllDataSelected,
			},
			actions: {
				handleRemoveFromList: (item: Commodity) => {
					const rawItem = data.find((d) => d.id === item.id);
					if (rawItem) {
						handleRemoveFromList(rawItem);
					}
				},
				handleAddAndClose: (item: Commodity) => {
					const rawItem =
						data.find((d) => d.id === item.id) ??
						(item as CommodityWithDefault);
					onAddAndClose?.(rawItem);
				},
				onToggleDefault: (item: CommodityWithDefault, isDefault: boolean) =>
					handleToggleDefault(item, isDefault),
			},
			editing: {
				enterEdit: (item: Commodity) => {
					const rawItem = data.find((d) => d.id === item.id);
					if (rawItem) {
						enterEdit(rawItem);
					}
				},
				handleCancelEdit: (item: Commodity) => {
					const rawItem = data.find((d) => d.id === item.id);
					if (rawItem) {
						handleCancelEdit(rawItem);
					}
				},
				handleSaveEdit: (item: Commodity) => {
					const rawItem = data.find((d) => d.id === item.id);
					if (rawItem) {
						handleSaveEdit(rawItem);
					}
				},
			},
		}),
		[
			handleCellSelect,
			handleHeaderCellSelect,
			setAllDataSelected,
			handleRemoveFromList,
			onAddAndClose,
			handleToggleDefault,
			enterEdit,
			handleCancelEdit,
			handleSaveEdit,
			data,
		],
	);

	const processedColumns = useCommoditiesColumns({
		state,
		data: columnData,
		handlers,
		visibleColumns,
		onColumnResize,
	});

	const { wrapperRef, distributedColumns } =
		useAutoResizeColumns(processedColumns);

	// When resize persistence is disabled, we auto-distribute widths to fit container size.
	// When enabled, we preserve user-managed widths from preference overlay and resize events.
	const autoDistributeWidths = !onColumnResize;
	const renderedColumns = autoDistributeWidths
		? distributedColumns
		: processedColumns;
	const trailingFixedColumnIds = useMemo(() => {
		const ids: string[] = [];
		for (let i = processedColumns.length - 1; i >= 0; i--) {
			const col = processedColumns[i];
			if (col?.reorderable !== false) {
				break;
			}
			ids.unshift(getStableColumnId(col as GridColumnProps, i));
		}
		return ids;
	}, [processedColumns]);

	const handleColumnReorderWithGuard = useCallback(
		(event: GridColumnReorderEvent) => {
			if (!onColumnReorder) return;

			if (trailingFixedColumnIds.length > 0) {
				const currentIds = event.columns.map((col, index) =>
					getStableColumnId(col, index),
				);
				const currentTrailingIds = currentIds.slice(
					-trailingFixedColumnIds.length,
				);
				const trailingFixedPreserved =
					currentTrailingIds.length === trailingFixedColumnIds.length &&
					currentTrailingIds.every(
						(id, index) => id === trailingFixedColumnIds[index],
					);

				if (!trailingFixedPreserved) {
					setReorderGuardKey((key) => key + 1);
					return;
				}
			}

			onColumnReorder(event);
		},
		[onColumnReorder, trailingFixedColumnIds],
	);
	// Changing this key forces the grid to remount when preference state versions change.
	// This ensures Kendo re-initializes with the latest saved column layout.
	const gridKey = `commodities-grid-${prefsGridKey}-${reorderGuardKey}`;

	const transformedData = useMemo(
		() => data.map((item) => transformCommodityForSearch(item)),
		[data],
	);

	return (
		<>
			<div
				ref={autoDistributeWidths ? wrapperRef : undefined}
				className={"commodities-grid-fill"}
			>
				<TraxGrid
					key={gridKey}
					className={`commodities-grid ${className ?? ""}`}
					columns={convertToGridColumnProps(renderedColumns)}
					data={transformedData}
					edit={edit}
					filter={filter}
					filterable={true}
					loading={isLoading || isLoadingPreference}
					loadingRows={take}
					// These callbacks are passed through from the parent, where reorder/resize
					// events are converted into persisted column settings (order + width).
					onColumnReorder={handleColumnReorderWithGuard}
					onColumnResize={onColumnResize}
					onFilterChange={handleFilterChange}
					onItemChange={(e) => {
						const dataItem = e.dataItem as Commodity;
						const rawItem = data.find((d) => d.id === dataItem.id) ?? dataItem;
						itemChange({ ...e, dataItem: rawItem });
					}}
					onPageChange={handlePageChange}
					onSortChange={handleSortChange}
					// Reordering is enabled only when a persistence handler exists.
					reorderable={Boolean(onColumnReorder)}
					resizable={true}
					skip={skip}
					sort={sort}
					style={{ ...style, height: "100%" }}
					take={take}
				/>
			</div>
			{showConfirmation ? (
				<TraxModal
					cancelText={"Cancel"}
					confirmText={"Yes, Delete"}
					confirmVariant={"primary"}
					onCancel={handleCancelDelete}
					onClose={handleCancelDelete}
					onConfirm={handleConfirmDelete}
					title={"Confirm Deletion"}
					type={"confirmation"}
				>
					Are you sure you want to remove &quot;{itemToDelete?.description}
					&quot; from your saved commodities?
				</TraxModal>
			) : null}
		</>
	);
};
